import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  CrawlScheduleService,
  type CrawlSchedule,
  type CrawlScheduleFrequency,
  type EnrichmentStep,
  type CrawlSchedulePatch
} from '../../services/crawl-schedule.service';
import { FeatureFlagService } from '../../services/feature-flag';
import { NotificationService } from '../../services/notification.service';

/**
 * Scheduled recrawls on Crawl Management — FW-CRAWL-DELTA-01 phase 6.
 * Spec: docs/features/MONITORING_CRAWL_DELTAS.md §6 phase 6.
 *
 * **Its own component for its own stylesheet**, the same reason
 * `crawl-run-history` is one: `crawl-management.css` shares the 90 KB
 * `anyComponentStyle` budget that `url-detail.css` already overshoots
 * (TECHNICAL_DEBT §0t), and the budget is per component.
 *
 * **Dev view only, and it gates itself.** The parent hides it too, but a
 * component that spends DataForSEO credit on a timer should not depend on one
 * `*ngIf` in a template it does not own — in user view this renders nothing and
 * never even asks the backend for the schedule.
 *
 * The status copy comes from the backend. The one thing rendered locally is the
 * next crawl time, because it is the one thing the backend cannot render: it is
 * stored as a UTC instant and belongs on screen in the viewer's timezone.
 */
@Component({
  selector: 'app-crawl-schedule',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './crawl-schedule.html',
  styleUrl: './crawl-schedule.css'
})
export class CrawlScheduleComponent implements OnChanges {
  @Input() domain: string | null = null;

  loading = false;
  saving = false;
  error: string | null = null;
  schedule: CrawlSchedule | null = null;
  expanded = false;

  /** Edits in progress. Kept apart from `schedule` so the status line on screen
   *  always describes what is saved, not what is typed. */
  draftFrequency: CrawlScheduleFrequency = 'manual';

  /**
   * The paid steps offered for standing consent, in dependency order.
   *
   * Top SERP rivals is deliberately absent: it carries its own ceiling and a
   * two-leg cost model, and hiding that behind a checkbox alongside these would
   * misrepresent what one tick authorizes.
   */
  readonly enrichmentSteps: ReadonlyArray<{ id: EnrichmentStep; label: string }> = [
    { id: 'estimated-keywords', label: 'Estimated keywords' },
    { id: 'url-serp-snapshots', label: 'URL SERP snapshots' },
    { id: 'supporting-pages-opportunities', label: 'Supporting-page opportunities (20–60 min)' }
  ];

  draftEnrichments: EnrichmentStep[] = [];
  /** `datetime-local` value — local wall clock, converted to UTC on save. */
  draftNextCrawlLocal = '';

  constructor(
    private schedules: CrawlScheduleService,
    private featureFlags: FeatureFlagService,
    private notifications: NotificationService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['domain']) {
      this.schedule = null;
      this.error = null;
      this.expanded = false;
      if (this.isDevView()) this.load();
    }
  }

  isDevView(): boolean {
    return !this.featureFlags.isProduction() && !this.featureFlags.isUserView();
  }

  load(): void {
    const domain = (this.domain || '').trim();
    if (!domain) return;

    this.loading = true;
    this.error = null;
    this.schedules.getForDomain(domain).subscribe({
      next: (response) => {
        this.applySchedule(response?.data ?? null);
        this.loading = false;
      },
      error: () => {
        this.error = 'The crawl schedule could not be loaded.';
        this.loading = false;
      }
    });
  }

  toggle(): void {
    this.expanded = !this.expanded;
  }

  private applySchedule(schedule: CrawlSchedule | null): void {
    this.schedule = schedule;
    this.draftFrequency = schedule?.frequency ?? 'manual';
    this.draftEnrichments = schedule?.autoEnrichments?.enabled
      ? [...(schedule.autoEnrichments.steps ?? [])]
      : [];
    this.draftNextCrawlLocal = this.toLocalInput(schedule?.nextCrawlAt ?? null);
  }

  /** ISO instant → the `YYYY-MM-DDTHH:mm` a `datetime-local` input wants, in
   *  the viewer's timezone. */
  startingFullCycle = false;

  /**
   * Start a full crawl now — the same shape a scheduled recrawl runs: focal
   * domain, every marked competitor, then the paid legs ticked below plus Top
   * SERP rivals.
   *
   * Dev-only for the moment because it spends without the priced confirm the
   * Setup card puts in front of the same work; the estimate belongs here before
   * this is shown to everyone.
   *
   * Confirms with the domain named, because "start a crawl" reading as "crawl
   * five domains and buy keywords for them" is exactly the surprise this whole
   * change is about removing.
   */
  startFullCycle(): void {
    if (!this.domain || this.startingFullCycle) return;
    const paid = [
      ...this.draftEnrichments.map((s) => s),
      ...(this.includeRivalsInFullCycle ? ['top SERP rivals'] : [])
    ];
    const ok = window.confirm(
      `Start a full crawl of ${this.domain} and its marked competitors?` +
        (paid.length ? `\n\nPaid steps: ${paid.join(', ')}.` : '\n\nNo paid steps selected.')
    );
    if (!ok) return;

    this.startingFullCycle = true;
    this.schedules
      .startFullCycle(this.domain, {
        enrichments: [...this.draftEnrichments],
        rivals: this.includeRivalsInFullCycle
      })
      .subscribe({
        next: () => {
          this.startingFullCycle = false;
          this.notifications.success(
            `Full crawl started for ${this.domain} — competitors are crawling too.`
          );
          this.load();
        },
        error: (err) => {
          this.startingFullCycle = false;
          this.notifications.error(
            err?.error?.error || err?.error?.message || 'Could not start the full crawl'
          );
        }
      });
  }

  /** Rivals is opt-in per run and never part of standing consent. */
  includeRivalsInFullCycle = false;

  isEnrichmentChecked(step: EnrichmentStep): boolean {
    return this.draftEnrichments.includes(step);
  }

  toggleEnrichment(step: EnrichmentStep): void {
    this.draftEnrichments = this.isEnrichmentChecked(step)
      ? this.draftEnrichments.filter((s) => s !== step)
      : [...this.draftEnrichments, step];
  }

  private toLocalInput(iso: string | null): string {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
      `T${pad(date.getHours())}:${pad(date.getMinutes())}`
    );
  }

  private save(patch: CrawlSchedulePatch): void {
    const domain = (this.domain || '').trim();
    if (!domain || this.saving) return;

    this.saving = true;
    this.error = null;
    this.schedules.update(domain, patch).subscribe({
      next: (response) => {
        this.applySchedule(response?.data ?? null);
        this.saving = false;
      },
      error: (response) => {
        // The backend's refusals are the useful ones — "next crawl time must be
        // in the future", "pick a cadence first" — so they are shown verbatim
        // rather than replaced with a generic failure.
        this.error = response?.error?.message || 'The schedule could not be saved.';
        this.saving = false;
      }
    });
  }

  /**
   * Frequency and an explicit next time save together, because picking "weekly"
   * and typing a start time is one intent, not two.
   *
   * Saving a cadence also switches the schedule **on**, and saving "off"
   * switches it off. Sending the frequency alone would leave a first-time save
   * showing "Paused — the weekly cadence is kept, but nothing will be started",
   * which is not what anyone means by picking Weekly. Pausing is what the Pause
   * button is for, and Resume undoes it; this control answers "how often",
   * which has no useful reading in which the answer is "never, but weekly".
   */
  saveSchedule(): void {
    const patch: CrawlSchedulePatch = {
      // Sent on every save, including when empty: an operator who unticks all
      // three is revoking consent, and omitting the key would leave it granted.
      autoEnrichments: {
        enabled: this.draftEnrichments.length > 0,
        steps: [...this.draftEnrichments]
      },
      frequency: this.draftFrequency,
      enabled: this.draftFrequency !== 'manual'
    };

    if (this.draftFrequency === 'manual') {
      this.save(patch);
      return;
    }

    const typed = this.draftNextCrawlLocal.trim();
    const current = this.schedule?.nextCrawlAt ?? null;
    if (typed) {
      const parsed = new Date(typed);
      if (Number.isNaN(parsed.getTime())) {
        this.error = 'That is not a valid date and time.';
        return;
      }
      // Only send it when it differs from what is stored: re-sending an
      // unchanged time that has since passed would be refused as being in the
      // past, on a save the operator did not think was about the time at all.
      if (!current || Math.abs(parsed.getTime() - new Date(current).getTime()) >= 60000) {
        patch.nextCrawlAt = parsed.toISOString();
      }
    }

    this.save(patch);
  }

  pause(): void {
    this.save({ enabled: false });
  }

  resume(): void {
    this.save({ enabled: true });
  }

  /** One week out from the time already scheduled, not from now — postponing a
   *  crawl that was due on Tuesday means next Tuesday. */
  postponeOneWeek(): void {
    const from = this.schedule?.nextCrawlAt ? new Date(this.schedule.nextCrawlAt) : new Date();
    const base = from.getTime() > Date.now() ? from : new Date();
    this.save({ nextCrawlAt: new Date(base.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString() });
  }

  canPause(): boolean {
    return !!this.schedule?.enabled;
  }

  canResume(): boolean {
    return !!this.schedule && !this.schedule.enabled && this.schedule.frequency !== 'manual';
  }

  statusClass(): string {
    return `crawl-schedule-status--${this.schedule?.status ?? 'off'}`;
  }

  /** The last crawl, named by who started it — a scheduled run and a run
   *  someone started by hand are the same document and different facts. */
  lastCrawlLine(): string | null {
    const last = this.schedule?.lastCrawl;
    if (!last) return null;
    const when = last.completedAt || last.startedAt;
    const prefix = last.source === 'scheduled' ? 'Last scheduled crawl' : 'Last crawl (started by hand)';
    return `${prefix} · ${last.status}${when ? '' : ' · no timestamp recorded'}`;
  }

  lastCrawlAt(): string | null {
    const last = this.schedule?.lastCrawl;
    return last?.completedAt || last?.startedAt || null;
  }
}
